import React from 'react'

import { ChartColumn, ChartLine, ChartPie, Disc } from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '~/client/src/shared/components/shadcn-ui/tabs'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { CHART_COLORS } from '../../Constants/Colors'
import { WidgetId, WIDGET_CONFIGS } from '../../Constants/WidgetConfig'

export interface ChartDataPoint {
  name: string
  value: number
  [key: string]: string | number // Index signature for Recharts compatibility
}

// Helper to render appropriate chart icon
const getChartTypeIcon = (chartType: string): React.ReactNode => {
  switch (chartType) {
    case 'pie_chart':
      return <ChartPie />
    case 'bar_chart':
      return <ChartColumn />
    case 'donut_chart':
      return <Disc />
    case 'line_chart':
      return <ChartLine />
    default:
      return null
  }
}

const getChartTypeLabel = (chartType: string): string => {
  const { chartTypes } = Localization.translator.dashboard
  switch (chartType) {
    case 'pie_chart':
      return chartTypes.pie
    case 'bar_chart':
      return chartTypes.bar
    case 'donut_chart':
      return chartTypes.donut
    case 'line_chart':
      return chartTypes.line
    default:
      return chartType
  }
}

// Helper method to get the active chart type for a widget
export const getWidgetChartType = (
  widgetId: WidgetId,
  widgetChartTypes: Record<string, string>,
): string => {
  const config = WIDGET_CONFIGS.find(c => c.id === widgetId)
  if (
    !config ||
    !config.availableChartTypes ||
    config.availableChartTypes.length === 0
  ) {
    return config?.type || '' // Return default type if no alternatives
  }
  // Return user's selected type or default to the widget's primary type
  return widgetChartTypes[widgetId] || config.type
}

// Helper to detect if data contains date/time-based names
const isDateBasedData = (data: ChartDataPoint[]): boolean => {
  if (data.length === 0) return false

  // Check if the first item's name looks like a date
  const firstItem = data[0].name

  // Common date patterns: MM/DD/YYYY, YYYY-MM-DD, Month Year, etc.
  const datePatterns = [
    /^\d{1,2}\/\d{1,2}\/\d{2,4}$/, // MM/DD/YYYY or M/D/YY
    /^\d{4}-\d{2}-\d{2}/, // YYYY-MM-DD
    /^\d{1,2}-\d{1,2}-\d{2,4}$/, // MM-DD-YYYY
    /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i, // Month names
    /^\d{4}$/, // Just year (YYYY)
    /^Q[1-4]\s\d{4}$/, // Quarter (Q1 2024)
  ]

  return datePatterns.some(pattern => pattern.test(firstItem))
}

// Helper to render chart type selector button
interface IChartTypeSelectorProps {
  widgetId: WidgetId
  currentType: string
  onChangeChartType: (widgetId: string, chartType: string) => void
}

export const ChartTypeSelector: React.FC<IChartTypeSelectorProps> = ({
  widgetId,
  currentType,
  onChangeChartType,
}) => {
  const config = WIDGET_CONFIGS.find(c => c.id === widgetId)
  if (
    !config ||
    !config.availableChartTypes ||
    config.availableChartTypes.length <= 1
  ) {
    return null // No selector needed if only one chart type
  }

  const availableTypes = config.availableChartTypes

  return (
    <Tabs
      value={currentType}
      onValueChange={value => onChangeChartType(widgetId, value as string)}
    >
      {/* The widget is drag-enabled by react-grid-layout; keep a press on any
        trigger from starting a drag. */}
      <TabsList
        aria-label={Localization.translator.dashboard.chartType}
        onMouseDown={e => e.stopPropagation()}
      >
        {availableTypes.map(chartType => (
          <TabsTrigger
            key={chartType}
            value={chartType}
            title={getChartTypeLabel(chartType)}
          >
            {getChartTypeIcon(chartType)}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  )
}

// Helper to render data as different chart types
interface IRenderChartByTypeProps {
  chartType: string
  data: ChartDataPoint[]
  chartName: string
}

export const renderChartByType = ({
  chartType,
  data,
  chartName,
}: IRenderChartByTypeProps): React.ReactNode => {
  if (data.length === 0) {
    return (
      <div className="no-data">{Localization.translator.noDataAvailable}</div>
    )
  }

  switch (chartType) {
    case 'pie_chart':
      return (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) =>
                `${name}: ${(percent * 100).toFixed(0)}%`
              }
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={CHART_COLORS[index % CHART_COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      )

    case 'donut_chart':
      return (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={80}
              labelLine={false}
              label={({ name, percent }) =>
                `${name}: ${(percent * 100).toFixed(0)}%`
              }
              fill="#8884d8"
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={CHART_COLORS[index % CHART_COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      )

    case 'bar_chart': {
      const isDateBased = isDateBasedData(data)

      // For date-based data, maintain chronological order (vertical bars)
      // For numeric data, sort by value descending (horizontal bars)
      const chartData = isDateBased
        ? data // Keep chronological order for dates
        : [...data].sort((a, b) => b.value - a.value) // Sort by value for numeric data

      if (isDateBased) {
        // Vertical bar chart for date-based data
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" fill={CHART_COLORS[2]} name={chartName} />
            </BarChart>
          </ResponsiveContainer>
        )
      } else {
        // Horizontal bar chart for numeric data (sorted by value)
        // Note: In Recharts, layout="vertical" creates horizontal bars
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={150} />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" fill={CHART_COLORS[2]} name={chartName} />
            </BarChart>
          </ResponsiveContainer>
        )
      }
    }

    default:
      return (
        <div className="no-data">
          {Localization.translator.dashboard.chartTypeNotSupported}
        </div>
      )
  }
}
